/**
 * Contracts between the API, the matchmaking worker and the rooms: queue tickets and room
 * assignments in Redis, the status the client polls, and the reports a room sends to the API
 * when a match ends or a player misbehaves. Zod at every boundary (CLAUDE.md conventions).
 */
import { z } from 'zod';
import { RoundEndReasonSchema, RoundRecordSchema, SlotSchema } from '../combat/schemas.js';
import { LoadoutSchema } from '../loadout/schemas.js';
const int = z.number().int();
export const QueueModeSchema = z.enum(['casual', 'ranked']);
export const OnlineModeSchema = z.enum(['casual', 'ranked', 'botFill']);
export const BotLevelSchema = z.enum(['easy', 'normal', 'hard']);
/** What a queued player asked for, stored under `MM_KEYS.ticket`. */
export const QueueTicketSchema = z
    .object({
    userId: z.string().min(1),
    callsign: z.string().min(1).max(32),
    mode: QueueModeSchema,
    mechId: z.string().min(1),
    /** The saved loadout resolved against ownership when the ticket was written (Phase 6). */
    loadout: LoadoutSchema.nullable(),
    /** Placeholder 1500 for everyone until Phase 5 brings Glicko-2. */
    rating: z.number().finite(),
    allowBot: z.boolean(),
    /** Milliseconds since the epoch. */
    enqueuedAt: int.nonnegative(),
    /** Refreshed by every status poll; stale tickets are evicted by the worker. */
    lastSeen: int.nonnegative(),
})
    .strict();
/** The seat a player was given, stored under `MM_KEYS.match` until the client collects it. */
export const MatchAssignmentSchema = z
    .object({
    roomId: z.string().min(1),
    matchId: z.string().min(1),
    /** The signed ticket that opens the seat (60 s). */
    ticket: z.string().min(1),
    slot: SlotSchema,
    mode: OnlineModeSchema,
    /** Mech ids by slot. */
    mechs: z.tuple([z.string().min(1), z.string().min(1)]),
    opponent: z
        .object({
        callsign: z.string().min(1).max(32),
        mechId: z.string().min(1),
        bot: BotLevelSchema.nullable(),
    })
        .strict(),
    createdAt: int.nonnegative(),
})
    .strict();
/** The match a user is seated in, stored under `MM_KEYS.active` while the room holds the seat. */
export const ActiveMatchSchema = z
    .object({
    roomId: z.string().min(1),
    matchId: z.string().min(1),
    /** Resumes the seat from any tab while the room still holds it. */
    reconnectionToken: z.string().min(1).nullable(),
    updatedAt: int.nonnegative(),
})
    .strict();
/** `POST /api/v1/match/queue`. */
export const QueueRequestSchema = z
    .object({
    mode: QueueModeSchema,
    mechId: z.string().min(1),
    /** Fill with a bot once the wait exceeds the queue's bot-fill delay. */
    allowBot: z.boolean(),
})
    .strict();
/** `GET /api/v1/match/status`, polled once a second by the queue screen. */
export const QueueStatusSchema = z.discriminatedUnion('state', [
    z
        .object({
        state: z.literal('idle'),
        /** A ranked leaver timeout still running (ms since the epoch), else null. */
        penaltyUntil: int.nonnegative().nullable(),
    })
        .strict(),
    z
        .object({
        state: z.literal('searching'),
        mode: QueueModeSchema,
        waitSeconds: z.number().nonnegative(),
        /** Rating band accepted right now (ranked), else null. */
        band: z.number().nonnegative().nullable(),
        allowBot: z.boolean(),
    })
        .strict(),
    z
        .object({
        state: z.literal('matched'),
        roomId: z.string().min(1),
        matchId: z.string().min(1),
        ticket: z.string().min(1),
        slot: SlotSchema,
        mode: OnlineModeSchema,
        mechs: z.tuple([z.string().min(1), z.string().min(1)]),
        opponent: MatchAssignmentSchema.shape.opponent,
    })
        .strict(),
    z
        .object({
        state: z.literal('inMatch'),
        roomId: z.string().min(1),
        matchId: z.string().min(1),
        reconnectionToken: z.string().min(1).nullable(),
    })
        .strict(),
]);
export const MatchOutcomeSchema = z.enum(['win', 'loss', 'draw', 'none']);
export const MatchEndReasonSchema = z.enum(['rounds', 'forfeit', 'aborted']);
/** One seat's outcome in a match report. */
export const MatchPlayerReportSchema = z
    .object({
    slot: SlotSchema,
    /** Null for the bot. */
    userId: z.string().min(1).nullable(),
    mechId: z.string().min(1),
    /** Optional so results already in a game server's outbox keep parsing (Phase 6). */
    loadout: LoadoutSchema.nullable().optional(),
    outcome: MatchOutcomeSchema,
    /** The section 7 score breakdown, or null when the match never produced one. */
    score: z.record(z.string(), z.unknown()).nullable(),
    stats: z.record(z.string(), z.unknown()),
    disconnects: int.nonnegative(),
})
    .strict();
/** `POST /api/v1/internal/match-results`: the server's verdict on a finished or aborted match. */
/** The room's event log is bounded on both ends by this one number (Phase 7). */
export const MATCH_EVENT_LOG_MAX = 2000;
/** One row of the event log the game server sends with a result (Phase 7). */
export const MatchEventRecordSchema = z
    .object({
    seq: int.nonnegative(),
    tick: int.nonnegative(),
    kind: z.string().min(1).max(40),
    slot: SlotSchema.nullable(),
    /** Small and flat: numbers, short strings, booleans, nulls (the room sanitises). */
    data: z.record(z.string().max(40), z.unknown()),
})
    .strict();
export const MatchResultReportSchema = z
    .object({
    /** The balance version the match ran under (Phase 7); absent or 0 = the built-in data. */
    balanceVersion: z.number().int().nonnegative().optional(),
    matchId: z.uuid(),
    roomId: z.string().min(1),
    mode: OnlineModeSchema,
    arenaId: z.string().min(1),
    seed: int.nonnegative(),
    reason: MatchEndReasonSchema,
    winner: SlotSchema.nullable(),
    /** The seat that left or never showed, for forfeits and aborts. */
    leaver: SlotSchema.nullable(),
    ticks: int.nonnegative(),
    finalHash: int.nonnegative(),
    /** Milliseconds since the epoch; null when the match never started. */
    startedAt: int.nonnegative().nullable(),
    endedAt: int.nonnegative(),
    /** Every round the match played, in order (empty for an abort before the countdown). */
    rounds: z.array(RoundRecordSchema).max(64),
    /** The bounded event log (Phase 7); absent from reports older than it. */
    events: z.array(MatchEventRecordSchema).max(MATCH_EVENT_LOG_MAX).optional(),
    players: z.array(MatchPlayerReportSchema).min(1).max(2),
})
    .strict();
export const AnomalySeveritySchema = z.enum(['soft', 'hard']);
export const AnomalyRecordSchema = z
    .object({
    kind: z.string().min(1).max(40),
    severity: AnomalySeveritySchema,
    count: int.positive(),
    firstTick: int.nonnegative(),
    lastTick: int.nonnegative(),
    sample: z.unknown().optional(),
})
    .strict();
/** `POST /api/v1/internal/anomalies`: one seat's validation failures, batched per room. */
export const AnomalyBatchSchema = z
    .object({
    matchId: z.uuid(),
    roomId: z.string().min(1),
    userId: z.string().min(1),
    slot: SlotSchema,
    records: z.array(AnomalyRecordSchema).min(1).max(64),
})
    .strict();
/** One recorded round, as `GET /api/v1/match/:id` returns it. */
export const MatchRoundDetailSchema = z
    .object({
    round: int.positive(),
    winnerSlot: int.nullable(),
    reason: RoundEndReasonSchema,
    ticks: int.nonnegative(),
    hpPct: z.tuple([z.number(), z.number()]),
})
    .strict();
/** One seat of a recorded match: the score the server computed and what the ledger paid. */
export const MatchSeatDetailSchema = z
    .object({
    slot: int.nonnegative(),
    mechId: z.string().min(1),
    loadout: LoadoutSchema.nullable(),
    outcome: MatchOutcomeSchema,
    bot: z.boolean(),
    /** The caller's own seat. */
    mine: z.boolean(),
    score: z.record(z.string(), z.unknown()).nullable(),
    scoreTotal: int.nullable(),
    /** Credits and XP the ledger holds for this seat; null until the match is settled. */
    rewards: z.object({ credits: int.nonnegative(), xp: int.nonnegative() }).strict().nullable(),
    /** Ranked only: the Glicko-2 rating either side of this match. */
    ratingBefore: z.number().nullable(),
    ratingAfter: z.number().nullable(),
})
    .strict();
/** `GET /api/v1/match/:id`: a recorded match as its participants (and staff) may see it. */
export const MatchDetailSchema = z
    .object({
    id: z.uuid(),
    mode: OnlineModeSchema,
    endReason: MatchEndReasonSchema,
    winnerSlot: int.nullable(),
    leaverSlot: int.nullable(),
    ticks: int.nonnegative(),
    startedAt: z.string().nullable(),
    endedAt: z.string(),
    /** When the payout step ran; null while the ledger has nothing for this match yet. */
    settledAt: z.string().nullable(),
    rounds: z.array(MatchRoundDetailSchema),
    players: z.array(MatchSeatDetailSchema).min(1).max(2),
})
    .strict();
/** One row of `GET /api/v1/match/history`: the caller's seat, as recorded and paid. */
export const MatchHistoryRowSchema = z
    .object({
    id: z.uuid(),
    mode: OnlineModeSchema,
    endReason: MatchEndReasonSchema,
    slot: int.nonnegative(),
    outcome: MatchOutcomeSchema,
    winnerSlot: int.nullable(),
    mechId: z.string().min(1),
    endedAt: z.string(),
    /** The payout step has run: `credits` and `xp` are what the ledger holds. */
    settled: z.boolean(),
    credits: int.nonnegative().nullable(),
    xp: int.nonnegative().nullable(),
    ratingBefore: z.number().nullable(),
    ratingAfter: z.number().nullable(),
    opponent: z
        .object({ callsign: z.string().nullable(), mechId: z.string().min(1), bot: z.boolean() })
        .strict()
        .nullable(),
})
    .strict();
export const MatchHistorySchema = z.object({ matches: z.array(MatchHistoryRowSchema) }).strict();
//# sourceMappingURL=schemas.js.map